import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Autocomplete, CircleF, GoogleMap, MarkerF, useJsApiLoader } from "@react-google-maps/api";
import { Building2, ChevronDown, ChevronUp, Loader2, MapPin, Search } from "lucide-react";
import { CoproRegistryEntryDto } from "api/referential";
import { referentialsApi } from "services/swagger-api";
import { GenericMessage } from "components/feedback/Message";
import { InputText } from "components/forms/InputText";
import { Button } from "components/ui/Button";
import GenericTabs from "components/navigation/Tabs/Tabs";
import { CopropertyRegistryCard } from "./CopropertyRegistryCard";
import { CopropertyAddressInput } from "./useCoproperty";

const GOOGLE_API_KEY = process.env.REACT_APP_GOOGLE_AUTOCOMPLETE_API_KEY ?? "";
const GOOGLE_LIBRARIES: ("places")[] = ["places"];

/** Rayon de recherche autour de l'adresse, borné 0–300 m (spec MRI). */
const DEFAULT_RADIUS_M = 100;
const MAX_RADIUS_M = 300;

const MAP_CONTAINER_STYLE = { width: "100%", height: "100%" };
const FRANCE_CENTER = { lat: 46.6, lng: 2.4 };

/**
 * Couleurs des repères du registre. Google Maps exige un hexadécimal : elles ne peuvent
 * pas venir des classes du thème, et la légende reprend les mêmes valeurs.
 */
const MARKER_COLOR_SYNDIC = "#10b981";
const MARKER_COLOR_DEFAULT = "#f59e0b";
const MARKER_COLOR_SELECTED = "#4f46e5";

/** Pastille de légende / de liste, à la couleur d'un repère de la carte. */
const MarkerDot: FC<{ color: string }> = ({ color }) => (
  <span
    aria-hidden
    className="inline-block h-2.5 w-2.5 shrink-0 rounded-full ring-1 ring-white"
    style={{ backgroundColor: color }}
  />
);

interface SelectedPoint {
  lat: number;
  lng: number;
  address: CopropertyAddressInput | null;
  formatted: string;
}

interface CopropertySearchProps {
  /** Copropriété du registre en cours de sélection (la page porte l'action de rattachement). */
  selectedEntry: CoproRegistryEntryDto | null;
  onSelectEntry: (entry: CoproRegistryEntryDto | null) => void;
  /** Création manuelle à l'adresse recherchée (copro absente du registre). */
  onCreateManual: (address: CopropertyAddressInput) => void;
  isSubmitting: boolean;
  /** SIRET du représentant (syndic) : pré-remplissage des copropriétés qu'il gère au registre. */
  syndicSiret?: string | null;
  /** Point de départ de la carte et de la recherche (copropriété déjà rattachée, en changement). */
  initialPoint?: { lat: number; lng: number; formatted: string } | null;
}

/** Extrait une adresse structurée d'un résultat Google Places. */
const toAddressInput = (place: google.maps.places.PlaceResult): CopropertyAddressInput | null => {
  const get = (type: string) =>
    place.address_components?.find((c) => c.types.includes(type))?.long_name ?? "";

  const streetNumber = get("street_number");
  const route = get("route");
  const line1 = [streetNumber, route].filter(Boolean).join(" ");
  const zipCode = get("postal_code");
  const city = get("locality") || get("postal_town");

  if (!line1 || !zipCode || !city) {
    return null;
  }

  const location = place.geometry?.location;
  return {
    line1,
    zipCode,
    city,
    country: "FR",
    formattedAddress: place.formatted_address ?? null,
    latitude: location?.lat() ?? null,
    longitude: location?.lng() ?? null,
    googlePlaceId: place.place_id ?? null,
  };
};

/**
 * Choix de la copropriété MRI : adresse (autocomplete Google) OU n° d'immatriculation,
 * carte permanente avec rayon paramétrable et repères des copropriétés du registre
 * national, panneau latéral : copropriétés du syndic (section repliable persistante),
 * liste des résultats toujours visible avec la ligne sélectionnée synchronisée à la
 * carte, résumé de la sélection, création manuelle. La sélection est contrôlée par la
 * page, dont le bouton d'étape est l'unique action de rattachement.
 */
export const CopropertySearch: FC<CopropertySearchProps> = ({
  selectedEntry,
  onSelectEntry,
  onCreateManual,
  isSubmitting,
  syndicSiret,
  initialPoint,
}) => {
  const { isLoaded: isMapReady } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_API_KEY,
    libraries: GOOGLE_LIBRARIES,
  });

  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const [point, setPoint] = useState<SelectedPoint | null>(
    initialPoint ? { lat: initialPoint.lat, lng: initialPoint.lng, address: null, formatted: initialPoint.formatted } : null
  );
  const [radius, setRadius] = useState(DEFAULT_RADIUS_M);
  const [entries, setEntries] = useState<CoproRegistryEntryDto[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [immatriculation, setImmatriculation] = useState("");
  const [immatriculationError, setImmatriculationError] = useState<string | null>(null);
  const [syndicEntries, setSyndicEntries] = useState<CoproRegistryEntryDto[]>([]);
  /** Section syndic ouverte au départ, repliée dès qu'une recherche commence (reste accessible). */
  const [isSyndicOpen, setIsSyndicOpen] = useState(true);
  const rowRefs = useRef<Map<string, HTMLLIElement>>(new Map());
  const mapRef = useRef<google.maps.Map | null>(null);
  /** Incrémenté par le bouton « Rechercher » pour forcer une relance à point/rayon constants. */
  const [searchTick, setSearchTick] = useState(0);

  // Pré-remplissage syndic : les copropriétés gérées par le représentant (SIRET apparié au registre).
  useEffect(() => {
    if (!syndicSiret) return;

    let cancelled = false;
    referentialsApi
      .getCoproRegistryBySiret({ siret: syndicSiret, maxResultCount: 50 })
      .then((result) => {
        if (!cancelled) setSyndicEntries(result);
      })
      .catch(() => {
        if (!cancelled) setSyndicEntries([]);
      });

    return () => {
      cancelled = true;
    };
  }, [syndicSiret]);

  const selectSyndicEntry = useCallback(
    (entry: CoproRegistryEntryDto) => {
      onSelectEntry(entry);
      setIsSyndicOpen(false);
      if (entry.latitude && entry.longitude) {
        setPoint({
          lat: entry.latitude,
          lng: entry.longitude,
          address: null,
          formatted: entry.adresse ?? "",
        });
      }
    },
    [onSelectEntry]
  );

  /**
   * Clic sur une ligne ou un repère : sélectionne, ou désélectionne si déjà sélectionnée.
   * La copropriété sélectionnée devient l'épicentre de la recherche (repère, rayon, résultats),
   * comme lors d'un choix dans la liste du syndic.
   */
  const toggleEntry = useCallback(
    (entry: CoproRegistryEntryDto) => {
      if (selectedEntry?.id === entry.id) {
        onSelectEntry(null);
        return;
      }
      onSelectEntry(entry);
      if (entry.latitude && entry.longitude) {
        setPoint({ lat: entry.latitude, lng: entry.longitude, address: null, formatted: entry.adresse ?? "" });
      }
    },
    [onSelectEntry, selectedEntry]
  );

  // Sélection depuis la carte : la ligne correspondante est amenée dans la zone visible.
  useEffect(() => {
    if (!selectedEntry?.id) return;
    rowRefs.current.get(selectedEntry.id)?.scrollIntoView({ block: "nearest" });
  }, [selectedEntry]);

  // Sélection d'une copropriété sans coordonnées : la carte reste où elle est ; avec
  // coordonnées, l'épicentre suit (voir toggleEntry) et la carte se recentre avec lui.
  useEffect(() => {
    if (!selectedEntry?.latitude || !selectedEntry?.longitude) return;
    mapRef.current?.panTo({ lat: selectedEntry.latitude, lng: selectedEntry.longitude });
  }, [selectedEntry]);

  // Copropriétés du registre dans le rayon — rechargées à chaque déplacement/rayon/relance.
  useEffect(() => {
    if (!point) return;

    let cancelled = false;
    setIsSearching(true);

    referentialsApi
      .getCoproRegistryWithinRadius({
        latitude: point.lat,
        longitude: point.lng,
        radiusInKm: radius / 1000,
        maxResultCount: 100,
      })
      .then((result) => {
        if (!cancelled) setEntries(result.filter((e) => e.latitude && e.longitude));
      })
      .catch(() => {
        if (!cancelled) setEntries([]);
      })
      .finally(() => {
        if (!cancelled) setIsSearching(false);
      });

    return () => {
      cancelled = true;
    };
  }, [point, radius, searchTick]);

  const handlePlaceChanged = useCallback(() => {
    const place = autocompleteRef.current?.getPlace();
    const location = place?.geometry?.location;
    if (!place || !location) return;

    onSelectEntry(null);
    setIsSyndicOpen(false);
    setPoint({
      lat: location.lat(),
      lng: location.lng(),
      address: toAddressInput(place),
      formatted: place.formatted_address ?? "",
    });
  }, [onSelectEntry]);

  const searchByImmatriculation = useCallback(async () => {
    const numero = immatriculation.trim();
    if (!numero) return;

    setImmatriculationError(null);
    try {
      const entry = await referentialsApi.getCoproRegistryByRegistrationNumber({
        numeroImmatriculation: numero,
      });

      if (!entry?.numeroImmatriculation) {
        setImmatriculationError("Aucune copropriété du registre ne porte ce numéro.");
        return;
      }

      onSelectEntry(entry);
      setIsSyndicOpen(false);
      if (entry.latitude && entry.longitude) {
        setPoint({
          lat: entry.latitude,
          lng: entry.longitude,
          address: null,
          formatted: entry.adresse ?? "",
        });
      }
    } catch {
      setImmatriculationError("Aucune copropriété du registre ne porte ce numéro.");
    }
  }, [immatriculation, onSelectEntry]);

  const mapCenter = useMemo(
    () => (point ? { lat: point.lat, lng: point.lng } : FRANCE_CENTER),
    [point]
  );

  /** SIRET du syndic sans espaces, pour l'apparier aux SIRET du registre. */
  const normalizedSyndicSiret = useMemo(
    () => (syndicSiret ? syndicSiret.replace(/\s/g, "") : null),
    [syndicSiret]
  );

  /** La copropriété est gérée par le syndic présélectionné du dossier. */
  const isSyndicEntry = useCallback(
    (entry: CoproRegistryEntryDto) =>
      normalizedSyndicSiret != null && entry.numeroSiret?.replace(/\s/g, "") === normalizedSyndicSiret,
    [normalizedSyndicSiret]
  );

  /** Couleur du repère : sélection, copropriété du syndic, autre. */
  const entryColor = useCallback(
    (entry: CoproRegistryEntryDto) =>
      selectedEntry?.id === entry.id
        ? MARKER_COLOR_SELECTED
        : isSyndicEntry(entry)
          ? MARKER_COLOR_SYNDIC
          : MARKER_COLOR_DEFAULT,
    [selectedEntry, isSyndicEntry]
  );

  return (
    <div className="overflow-hidden rounded-xl bg-white shadow">
      <div className="px-5 pt-4">
        <GenericMessage
          message="Recherchez la copropriété dans le registre national, ou créez-la si elle n'y figure pas."
          type="info"
          dismissible={false}
        />
      </div>

      <GenericTabs className="[&>ul]:px-5" fullWidth={false} defaultTab="address">
        <GenericTabs.Item label="Par adresse" value="address" />
        <GenericTabs.Item label="Par n° d'immatriculation" value="registration" />

        <GenericTabs.Content value="address">
          <div className="flex flex-col gap-4 px-4 lg:flex-row lg:items-end">
            <div className="min-w-0 flex-1">
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Adresse de la copropriété
              </label>
              {isMapReady ? (
                <Autocomplete
                  onLoad={(autocomplete) => {
                    autocompleteRef.current = autocomplete;
                  }}
                  onPlaceChanged={handlePlaceChanged}
                  options={{ componentRestrictions: { country: "fr" } }}
                >
                  <div className="relative">
                    <MapPin className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Rechercher une adresse…"
                      className="h-12 w-full rounded-lg border border-gray-300 pl-9 pr-4 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                </Autocomplete>
              ) : (
                <div className="flex h-12 items-center gap-2 rounded-lg border border-gray-200 px-4 text-sm text-gray-400">
                  <Loader2 className="h-4 w-4 animate-spin" /> Chargement de Google Maps…
                </div>
              )}
            </div>

            <div className="w-full lg:w-52">
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Rayon de recherche : {radius} m
              </label>
              <input
                type="range"
                min={0}
                max={MAX_RADIUS_M}
                step={25}
                value={radius}
                onChange={(e) => setRadius(Number(e.target.value))}
                className="h-12 w-full accent-primary"
              />
            </div>

            <Button
              color="primary"
              disabled={!point || isSearching}
              onClick={() => setSearchTick((tick) => tick + 1)}
              className="h-12 shrink-0"
            >
              {isSearching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search size={16} />}
              Rechercher
            </Button>
          </div>
        </GenericTabs.Content>

        <GenericTabs.Content value="registration">
          <div className="flex flex-col gap-4 px-4 lg:flex-row lg:items-end">
            <div className="min-w-0 flex-1">
              <InputText
                label="N° d'immatriculation au registre"
                placeholder="AA1234567"
                value={immatriculation}
                error={immatriculationError ?? undefined}
                onChange={(e) => setImmatriculation(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && searchByImmatriculation()}
              />
            </div>
            <Button color="primary" onClick={searchByImmatriculation} className="h-12 shrink-0">
              <Search size={16} />
              Rechercher
            </Button>
          </div>
        </GenericTabs.Content>
      </GenericTabs>

      {/* Carte permanente + panneau de résultats */}
      <div className="grid grid-cols-1 gap-6 px-4 pb-4 lg:grid-cols-[2fr_1fr]">
        <div className="flex flex-col gap-2">
          <div className="h-[480px] overflow-hidden rounded-2xl border border-gray-200 bg-gray-100">
            {isMapReady ? (
              <GoogleMap
                mapContainerStyle={MAP_CONTAINER_STYLE}
                center={mapCenter}
                zoom={point ? 17 : 6}
                onLoad={(map) => {
                  mapRef.current = map;
                }}
                onUnmount={() => {
                  mapRef.current = null;
                }}
              >
                {point && (
                  <>
                    <MarkerF position={mapCenter} zIndex={2} />
                    <CircleF
                      center={mapCenter}
                      radius={radius}
                      options={{
                        fillColor: "#6366f1",
                        fillOpacity: 0.08,
                        strokeColor: "#6366f1",
                        strokeWeight: 1.5,
                      }}
                    />
                  </>
                )}
                {entries.map((entry) => (
                  <MarkerF
                    key={entry.id}
                    position={{ lat: entry.latitude!, lng: entry.longitude! }}
                    // `icon` n'est appliquée qu'au montage par MarkerF (2.19) : la couleur de
                    // sélection passe par `options`, réappliquées à chaque changement.
                    options={{
                      icon: {
                        path: google.maps.SymbolPath.CIRCLE,
                        scale: 8,
                        fillColor: entryColor(entry),
                        fillOpacity: 1,
                        strokeColor: "#ffffff",
                        strokeWeight: 2,
                      },
                      zIndex: selectedEntry?.id === entry.id ? 3 : 1,
                    }}
                    title={entry.nomCopropriete ?? entry.adresse ?? undefined}
                    onClick={() => toggleEntry(entry)}
                  />
                ))}
              </GoogleMap>
            ) : (
              <div className="flex h-full items-center justify-center gap-2 text-sm text-gray-400">
                <Loader2 className="h-4 w-4 animate-spin" /> Chargement de Google Maps…
              </div>
            )}
          </div>

          {normalizedSyndicSiret && (
            <ul className="flex flex-wrap items-center gap-x-4 gap-y-1 px-1 text-xs text-gray-600">
              <li className="flex items-center gap-1.5">
                <MarkerDot color={MARKER_COLOR_SYNDIC} />
                Copropriétés du syndic sélectionné
              </li>
              <li className="flex items-center gap-1.5">
                <MarkerDot color={MARKER_COLOR_DEFAULT} />
                Autres copropriétés
              </li>
              <li className="flex items-center gap-1.5">
                <MarkerDot color={MARKER_COLOR_SELECTED} />
                Sélection
              </li>
            </ul>
          )}
        </div>

        {/* Panneau latéral : syndic (repliable), résultats toujours visibles, sélection, création manuelle */}
        <div className="flex h-[480px] flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white">
          {syndicEntries.length > 0 && (
            <div className={`shrink-0 border-b border-gray-100 ${isSyndicOpen ? "flex min-h-0 flex-col" : ""}`}>
              <button
                type="button"
                onClick={() => setIsSyndicOpen((open) => !open)}
                aria-expanded={isSyndicOpen}
                className="flex w-full shrink-0 items-center justify-between gap-2 px-4 py-3 text-left text-sm font-semibold text-gray-900 hover:bg-gray-50"
              >
                <span className="flex min-w-0 items-center gap-2">
                  <Building2 className="h-4 w-4 shrink-0 text-primary" />
                  <span className="truncate">
                    Copropriétés gérées par {syndicEntries[0].representantLegal || "ce syndic"} (
                    {syndicEntries.length})
                  </span>
                </span>
                {isSyndicOpen ? (
                  <ChevronUp className="h-4 w-4 shrink-0 text-gray-400" />
                ) : (
                  <ChevronDown className="h-4 w-4 shrink-0 text-gray-400" />
                )}
              </button>
              {isSyndicOpen && (
                <ul className="max-h-56 divide-y divide-gray-100 overflow-y-auto border-t border-gray-100">
                  {syndicEntries.map((entry) => (
                    <li key={entry.id}>
                      <button
                        type="button"
                        onClick={() => selectSyndicEntry(entry)}
                        className={`flex w-full items-center justify-between gap-4 px-4 py-2.5 text-left text-sm hover:bg-gray-50 ${
                          selectedEntry?.id === entry.id ? "bg-primary/5" : ""
                        }`}
                      >
                        <span className="min-w-0">
                          <span className="block truncate font-medium text-gray-900">
                            {entry.nomCopropriete || entry.adresse}
                          </span>
                          <span className="block truncate text-xs text-gray-500">{entry.adresse}</span>
                        </span>
                        <span className="shrink-0 text-xs text-gray-400">{entry.numeroImmatriculation}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {point ? (
            <>
              <div className="shrink-0 border-b border-gray-100 px-4 py-3">
                <p className="text-sm font-semibold text-gray-900">
                  {isSearching
                    ? "Recherche des copropriétés…"
                    : entries.length === 0
                      ? "Aucune copropriété trouvée"
                      : `${entries.length} copropriété(s) à ${radius} m`}
                </p>
                <p className="text-xs text-gray-500">
                  {entries.length === 0 && !isSearching
                    ? "Élargissez le rayon ou saisissez le n° d'immatriculation."
                    : "Cliquez sur une ligne ou un repère de la carte pour la sélectionner."}
                </p>
              </div>

              <ul
                role="listbox"
                aria-label="Copropriétés du registre dans le rayon"
                className="min-h-0 flex-1 divide-y divide-gray-100 overflow-y-auto"
              >
                {entries.map((entry) => {
                  const isSelected = selectedEntry?.id === entry.id;
                  return (
                    <li
                      key={entry.id}
                      ref={(node) => {
                        if (!entry.id) return;
                        if (node) rowRefs.current.set(entry.id, node);
                        else rowRefs.current.delete(entry.id);
                      }}
                      role="option"
                      aria-selected={isSelected}
                    >
                      <button
                        type="button"
                        onClick={() => toggleEntry(entry)}
                        className={`flex w-full flex-col gap-0.5 border-l-4 px-3 py-2.5 text-left text-sm hover:bg-gray-50 ${
                          isSelected ? "border-primary bg-primary/5" : "border-transparent"
                        }`}
                      >
                        <span className="flex min-w-0 items-center gap-2">
                          <MarkerDot color={entryColor(entry)} />
                          <span className="truncate font-medium text-gray-900">
                            {entry.nomCopropriete || entry.adresse}
                          </span>
                        </span>
                        <span className="truncate text-xs text-gray-500">
                          {entry.adresse} · {entry.numeroImmatriculation}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>

              {selectedEntry ? (
                <div className="flex max-h-[60%] shrink-0 flex-col border-t border-gray-100 p-3">
                  <CopropertyRegistryCard entry={selectedEntry} onClear={() => onSelectEntry(null)} />
                </div>
              ) : (
                point.address && (
                  <div className="shrink-0 border-t border-gray-100 p-3">
                    <div className="rounded-xl border border-dashed border-gray-300 p-3">
                      <p className="text-xs text-gray-600">
                        Absente du registre ? Créez-la à l'adresse recherchée :
                      </p>
                      <p className="mt-1 truncate text-sm font-semibold text-gray-900">
                        {point.address.line1}, {point.address.zipCode} {point.address.city}
                      </p>
                      <Button
                        outline
                        color="primary"
                        disabled={isSubmitting}
                        onClick={() => onCreateManual(point.address!)}
                        className="mt-2 w-full"
                      >
                        Créer cette copropriété
                      </Button>
                    </div>
                  </div>
                )
              )}
            </>
          ) : (
            <div className="flex flex-1 flex-col items-center justify-center gap-2 p-6 text-center">
              <MapPin className="h-6 w-6 text-gray-300" />
              <p className="text-sm text-gray-500">
                {syndicEntries.length > 0
                  ? "Choisissez une copropriété du syndic, ou recherchez une adresse ou un n° d'immatriculation."
                  : "Recherchez une adresse ou un n° d'immatriculation pour afficher les copropriétés du registre."}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
